import { Component, inject, OnInit } from '@angular/core';
import { CreateUserDTO, User } from '../../model/user';
import { CompanyService } from 'src/app/admin/services/company.service';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ButtonModule } from 'primeng/button';
import { ConfirmationService } from 'primeng/api';
import { NotificationService } from 'src/app/core/services/notification.service';
import { UserService } from 'src/app/admin/services/user.service';
import { UserFormPopupComponent } from '../user-form-popup/user-form-popup.component';
import { firstValueFrom } from 'rxjs';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { CurrentUserService } from 'src/app/shared/services/current-user.service';
import { LucideAngularModule, Mail, Tag } from 'lucide-angular';
import { PackLimitationsService } from 'src/app/shared/services/pack-limitations.service';
import { LockedButtonComponent } from 'src/app/shared/components/locked-button/locked-button.component';
import { LimitedFeatures } from 'src/app/shared/models/pack.models';
import { UserLimitIndicatorComponent } from '../user-limit-indicator/user-limit-indicator.component';

@Component({
  selector: 'app-manage-company-users',
  imports: [
    ConfirmDialogModule,
    ButtonModule,
    UserFormPopupComponent,
    TranslocoModule,
    LucideAngularModule,
    LockedButtonComponent,
    UserLimitIndicatorComponent
  ],
  providers: [ConfirmationService],
  templateUrl: './manage-company-users.component.html',
  styleUrl: './manage-company-users.component.scss',
})
export class ManageCompanyUsersComponent implements OnInit {
  userList: User[] = [];
  companyService = inject(CompanyService);
  confirmationService = inject(ConfirmationService);
  notificationService = inject(NotificationService);
  userService = inject(UserService);
  currentUserService = inject(CurrentUserService);
  translocoService = inject(TranslocoService);
  packLimitationService = inject(PackLimitationsService);
  companyId?: string;
  isLoading = false;
  isLoadingUsers = true;

  readonly mailIcon = Mail;
  readonly tagIcon = Tag;

  createUserFeature = LimitedFeatures.CreateUser;

  getInitials(user: User): string {
    const first = user.firstName?.charAt(0)?.toUpperCase() || '';
    const last = user.lastName?.charAt(0)?.toUpperCase() || '';
    return first + last || '?';
  }

  ngOnInit() {
    this.loadUsers();
  }
  async loadUsers() {
    this.isLoadingUsers = true;
    this.companyService.getUserList().subscribe({
      next: (userList) => {
        this.userList = userList;
        this.companyId = this.userList[0]?.companyId;
        this.isLoadingUsers = false;
      },
      error: async () => {
        const errorMsg = await firstValueFrom(
          this.translocoService.selectTranslate('userAccount.loadUsers.error'),
        );
        this.notificationService.error(errorMsg);
        this.isLoadingUsers = false;
      },
    });
  }
  async createUser(userDTO: CreateUserDTO) {
    this.isLoading = true;
    if (!this.companyId) {
      const errorMsg = await firstValueFrom(
        this.translocoService.selectTranslate(
          'accountMenu.usersManagement.companyIdNotSet',
        ),
      );
      this.notificationService.error(errorMsg);
      this.isLoading = false;
      return;
    }
    const userDTOWithCompany = {
      ...userDTO,
      companyId: this.companyId,
    };
    this.userService.create(userDTOWithCompany).subscribe({
      next: async (response) => {
        this.userList.push(response);
        // Refresh the cached company._count.users used by the pack limit indicator
        this.currentUserService.refreshCurrentUser();
        const successMsg = await firstValueFrom(
          this.translocoService.selectTranslate(
            'accountMenu.usersManagement.userCreated',
          ),
        );
        this.notificationService.success(successMsg);
        this.isLoading = false;
      },
      error: async () => {
        const errorMsg = await firstValueFrom(
          this.translocoService.selectTranslate(
            'accountMenu.usersManagement.userCreateError',
          ),
        );
        this.notificationService.error(errorMsg);
        this.isLoading = false;
      },
    });
  }
  editUser(userDTO: CreateUserDTO, userid: string) {
    this.isLoading = true;
    this.userService.update(userid, userDTO).subscribe({
      next: async (response) => {
        const index = this.userList.findIndex((user) => user.id === userid);
        if (index !== -1) {
          this.userList[index] = response;
        }
        const successMsg = await firstValueFrom(
          this.translocoService.selectTranslate(
            'accountMenu.usersManagement.userUpdated',
          ),
        );
        this.notificationService.success(successMsg);
        this.isLoading = false;
      },
      error: async () => {
        const errorMsg = await firstValueFrom(
          this.translocoService.selectTranslate(
            'accountMenu.usersManagement.userUpdateError',
          ),
        );
        this.notificationService.error(errorMsg);
        this.isLoading = false;
      },
    });
  }
  async confirmDelete(userId: string) {
    const message = await firstValueFrom(
      this.translocoService.selectTranslate(
        'accountMenu.usersManagement.confirmDelete',
      ),
    );
    const header = await firstValueFrom(
      this.translocoService.selectTranslate(
        'accountMenu.usersManagement.deleteConfirmation',
      ),
    );
    const acceptLabel = await firstValueFrom(
      this.translocoService.selectTranslate(
        'accountMenu.usersManagement.confirmYes',
      ),
    );
    const rejectLabel = await firstValueFrom(
      this.translocoService.selectTranslate(
        'accountMenu.usersManagement.confirmNo',
      ),
    );
    this.confirmationService.confirm({
      message,
      header,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel,
      rejectLabel,
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => this.deleteUser(userId),
    });
  }

  deleteUser(userId: string) {
    this.userService.delete(userId).subscribe({
      next: async (response) => {
        this.userList = this.userList.filter((user) => user.id !== response.id);
        // Refresh the cached company._count.users used by the pack limit indicator
        this.currentUserService.refreshCurrentUser();
        const successMsg = await firstValueFrom(
          this.translocoService.selectTranslate(
            'accountMenu.usersManagement.userDeleted',
          ),
        );
        this.notificationService.success(successMsg);
      },
      error: async () => {
        const errorMsg = await firstValueFrom(
          this.translocoService.selectTranslate(
            'accountMenu.usersManagement.userDeleteError',
          ),
        );
        this.notificationService.error(errorMsg);
      },
    });
  }
}
